import { Typography, Link, Stack, FormControlLabel } from '@mui/material';
import { CheckboxField } from '@procureai/ui-form-mui';
import { isEmpty, isNil } from 'ramda';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

import {
  IntakeExtendedLineItemsCheckStepFormData,
  IntakeSelectableExtendedLineItemData,
} from '../IntakeExtendedLineItemsCheckStep.schema';
import { SvbrgContractItem } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { RouteId } from '~/routes/RouteId';

interface ContractItemsListProps {
  checkId: string;
  items: SvbrgContractItem[];
}

export const ContractItemsList = ({ checkId, items }: ContractItemsListProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeExtendedLineItemsCheckStep' });
  const navigate = useNavigate();

  if (isNil(items) || isEmpty(items)) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('noContractResults')}
      </Typography>
    );
  }

  return (
    <Stack spacing={2} width={'100%'}>
      <Typography variant="subtitle2">{t('contractResults')}</Typography>
      <Stack spacing={1}>
        {items.map((item) => {
          const itemLink = RouteId.contractDetail(item.contract.id);
          return <ContractItem key={item.id} checkId={checkId} item={item} itemLink={itemLink} navigate={navigate} />;
        })}
      </Stack>
    </Stack>
  );
};

interface ContractItemProps {
  checkId: string;
  item: SvbrgContractItem;
  itemLink: string;
  navigate: ReturnType<typeof useNavigate>;
}

const ContractItem = ({ checkId, item, itemLink }: ContractItemProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeExtendedLineItemsCheckStep' });
  const { control, watch } = useFormContext<IntakeExtendedLineItemsCheckStepFormData>();

  // Watch the selected item id for this checkId
  const selectedItem = watch(`items.${checkId}`);

  // Map SvbrgContractItem to expected form shape
  // Position will be assigned by the check
  const mapToFormShape = (): IntakeSelectableExtendedLineItemData => ({
    title: item.title,
    pricePerUnit: item.pricePerUnitFloat.toString(),
    quantity: item.plannedQuantityFloat?.toString() ?? undefined,
    id: item.id,
    currency: item.contract.currency ?? 'EUR',
    supplierId: item.contract.supplierId ?? undefined,
  });
  const isChecked = typeof selectedItem !== 'string' && selectedItem?.id === item.id;

  return (
    <Stack direction="row" justifyContent={'space-between'} alignItems="center" spacing={2}>
      <Stack direction="column">
        <Link component={RouterLink} to={itemLink} variant="subtitle1" underline="hover">
          {item.title}
        </Link>
      </Stack>
      <Controller
        name={`items.${checkId}`}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <CheckboxField
                value={isChecked}
                onChange={(checked: boolean | null) => {
                  if (checked) {
                    field.onChange(mapToFormShape());
                  } else {
                    field.onChange(undefined);
                  }
                }}
              />
            }
            label={t('button.select')}
          />
        )}
      />
    </Stack>
  );
};
